import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, PieChart, Loader2, Package, Warehouse } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';

const ProcessingClosingStock = () => {
  const navigate = useNavigate();
  const [stockData, setStockData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchClosingStock();
  }, []);

  const fetchClosingStock = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/processing/report/closing-stock');
      setStockData(response.data);
    } catch (error) {
      console.error('Error fetching closing stock:', error);
      toast.error('Failed to load closing stock');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FFFBF5] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D5F4F]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/sections/processing')}
              className="text-white hover:bg-[#2D5F4F]"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold">Closing Stock Report</h1>
              <p className="text-sm text-gray-400">Processing Section</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchClosingStock} className="text-white border-white hover:bg-white/10">
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Package className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Bundles</p>
                  <p className="text-3xl font-bold text-purple-600">{stockData?.total_bundles || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <Warehouse className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Processing Units</p>
                  <p className="text-3xl font-bold text-blue-600">{stockData?.unit_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Last Updated</p>
                  <p className="text-lg font-medium text-green-600">{new Date().toLocaleTimeString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Stock by Processing Unit */}
        <Card className="bg-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Warehouse className="w-5 h-5" />
              Stock by Processing Unit
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!stockData?.stock_by_unit || Object.keys(stockData.stock_by_unit).length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No bundles currently in processing</p>
              </div>
            ) : (
              <div className="space-y-4">
                {Object.entries(stockData.stock_by_unit).map(([unitName, data]) => (
                  <div key={unitName} className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-gray-800">{unitName}</h3>
                      <Badge className="bg-purple-100 text-purple-800">
                        {data.bundle_count} bundles
                      </Badge>
                    </div>
                    
                    {/* Design-wise breakdown */}
                    <div className="flex flex-wrap gap-2">
                      {(() => {
                        // Group bundles by design
                        const designCounts = {};
                        data.bundles?.forEach(bundle => {
                          const design = bundle.design_name || 'Unknown';
                          designCounts[design] = (designCounts[design] || 0) + 1;
                        });
                        
                        return Object.entries(designCounts).map(([design, count]) => (
                          <Badge key={design} variant="outline" className="bg-gray-50">
                            {design}: {count}
                          </Badge>
                        ));
                      })()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ProcessingClosingStock;
